import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ExchangeSelection from './ExchangeSelection';
import EnterUID from './EnterUID';
import CashbackInfo from './CashbackInfo';
import CompleteStep from './CompleteStep';
import { useTranslation } from '../../hooks/useTranslation';
import { showTelegramAlert } from '../../services/telegramService';
import { useAuthStore } from '../../store/authStore';
import { exchangeApi } from '../../services/api';
import type { Exchange } from '../../types';

interface ConnectExchangeProps {
  onComplete?: () => void;
  onCancel: () => void;
  onBackToList?: () => void;
}

const ConnectExchange = ({ onCancel, onBackToList: onBackToListProp }: ConnectExchangeProps) => {
  const { user } = useAuthStore();
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedExchangeId, setSelectedExchangeId] = useState<string | null>(null);
  const [selectedExchange, setSelectedExchange] = useState<Exchange | null>(null);
  const [uid, setUid] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (selectedExchangeId) {
      loadExchangeDetails();
    }
  }, [selectedExchangeId]);

  const loadExchangeDetails = async () => {
    if (!selectedExchangeId) return;
    try {
      const response = await exchangeApi.getExchanges();
      const exchange = response.exchanges.find((e: Exchange) => e.id === selectedExchangeId);
      if (exchange) {
        setSelectedExchange(exchange);
      }
    } catch (error) {
      console.error('Error loading exchange details:', error);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!selectedExchangeId) {
        showTelegramAlert(t('linkExchange.selectExchange'));
        return;
      }
      // Move to step 2 (Enter UID) after selecting exchange
      setStep(2);
    } else if (step === 2) {
      if (!uid.trim()) {
        showTelegramAlert(t('linkExchange.enterUID'));
        return;
      }
      
      // Check if user has email
      if (!user || !user.email || !user.email.trim()) {
        showTelegramAlert(t('settings.emailVerification'));
        return;
      }
      
      // Step 3: Complete - Link the account
      if (!user || !selectedExchange || !uid.trim()) return;
      
      setLinking(true);
      try {
        const { exchangeApi: exchangeApiService } = await import('../../services/api');
        await exchangeApiService.linkAccount({
          exchange: selectedExchange.code as 'binance' | 'bybit' | 'okx' | 'gate',
          uid: uid.trim()
        });
        setStep(3);
      } catch (error: any) {
        const errorMessage = error.message || t('common.error');
        showTelegramAlert(errorMessage);
        
        // If error is about email, navigate to settings with profile tab
        if (errorMessage.includes('email') || errorMessage.includes('Email')) {
          setTimeout(() => {
            navigate('/settings?tab=profile');
          }, 1500);
        }
      } finally {
        setLinking(false);
      }
    }
  };

  const handleBack = () => {
    if (step === 1) {
      onCancel();
    } else {
      setStep((step - 1) as 1 | 2);
    }
  };

  const handleRegister = () => {
    // Open exchange registration page
    if (selectedExchange) {
      // Use registerUrl from database if available, otherwise fallback to default URLs
      const registerUrl = selectedExchange.cashbackInfo?.registerUrl;
      if (registerUrl && registerUrl.trim() !== '') {
        const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
        if (tg && tg.openLink) {
          tg.openLink(registerUrl);
        } else {
          window.open(registerUrl, '_blank', 'noopener,noreferrer');
        }
      } else {
        // Fallback to default URLs if registerUrl is not set
        const urls: Record<string, string> = {
          okx: 'https://www.okx.com',
          bybit: 'https://www.bybit.com',
          binance: 'https://www.binance.com',
          gate: 'https://www.gate.io',
        };
        const url = urls[selectedExchange.code] || urls.okx;
        const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
        if (tg && tg.openLink) {
          tg.openLink(url);
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      }
    }
  };


  const handleBackToList = () => {
    if (onBackToListProp) {
      onBackToListProp();
    } else {
      onCancel();
    }
  };

  const steps = [
    { id: 1, label: t('linkExchange.linkAccount'), icon: '✏️' },
    { id: 2, label: t('linkExchange.enterUID'), icon: '👤' },
    { id: 3, label: t('rebate.completed'), icon: '✓' },
  ];

  return (
    <div className="p-4 pb-24">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={handleBack}
          className="text-tg-hint hover:text-primary transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-2xl font-bold text-white">
          {t('linkExchange.title')}
        </h1>
      </div>

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          {steps.map((s, index) => {
            const isActive = step === s.id;
            const isCompleted = step > s.id;
            return (
              <div key={s.id} className="flex items-center flex-1" style={{ minWidth: 0 }}>
                <div className="flex flex-col items-center w-full">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all relative ${
                      isActive || isCompleted
                        ? 'bg-primary text-black'
                        : 'bg-tg-secondary text-tg-hint'
                    }`}
                  >
                    {isCompleted || (step === 3 && s.id === 3) ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : s.id === 1 ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    ) : s.id === 2 ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {step === 3 && s.id === 3 && (
                      <div className="absolute inset-0 bg-primary rounded-full opacity-30 animate-ping" />
                    )}
                  </div>
                  <div
                    className={`text-xs mt-2 font-medium text-center whitespace-nowrap ${
                      isActive || (step === 3 && s.id === 3) ? 'text-primary' : isCompleted ? 'text-primary' : 'text-tg-hint'
                    }`}
                  >
                    {s.label}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-2 mt-5 transition-all ${
                      isCompleted ? 'bg-primary' : 'bg-gray-700'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="mb-6">
        {step === 1 && (
          <div className="space-y-6">
            <ExchangeSelection
              selectedExchange={selectedExchangeId}
              onSelect={setSelectedExchangeId}
              language={language}
            />
            {selectedExchange && selectedExchange.cashbackInfo && (
              <CashbackInfo
                exchange={selectedExchange}
                language={language}
                onRegister={handleRegister}
              />
            )}
          </div>
        )}
        {step === 2 && selectedExchange && (
          <EnterUID
            exchange={selectedExchange.code}
            uid={uid}
            onUIDChange={setUid}
            language={language}
            hasEmail={!!(user?.email && user.email.trim())}
            onNavigateToSettings={() => navigate('/settings?tab=profile')}
          />
        )}
        {step === 3 && selectedExchange && (
          <CompleteStep
            exchange={selectedExchange.code}
            language={language}
            onBackToList={handleBackToList}
          />
        )}
      </div>

      {/* Navigation Buttons - Only show for steps 1 and 2 */}
      {step < 3 && (
        <div className="flex gap-3">
          <button
            onClick={handleBack}
            className="flex-1 py-3 bg-tg-secondary text-white rounded-xl text-base font-semibold flex items-center justify-center gap-2 transition-opacity active:opacity-80"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </button>
          <button
            onClick={handleNext}
            disabled={linking || (step === 1 && !selectedExchangeId)}
            className="flex-1 py-3 bg-primary text-black rounded-xl text-base font-semibold flex items-center justify-center gap-2 transition-opacity active:opacity-80 disabled:opacity-60"
          >
            {linking 
              ? t('common.loading')
              : t('common.next')}
            {!linking && (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConnectExchange;

